import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Paper,
} from "@mui/material";

/**
 * PracticeAreaLayout Component
 *
 * A reusable component for practice area pages that provides a consistent layout
 * with an image on the left and content on the right for desktop views,
 * and stacked for mobile views.
 *
 * @param {Object} props
 * @param {string} props.title - The title of the practice area
 * @param {string} props.content - The main content text
 * @param {string} props.image - The image URL
 * @param {string} props.imageAlt - Alt text for the image
 * @param {string} props.buttonText - Text for the CTA button
 * @param {string} props.buttonLink - Link for the CTA button
 */
function PracticeAreaLayout({
  title,
  content,
  image,
  imageAlt,
  buttonText,
  buttonLink,
}) {
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>

      <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 2 }}>
        <Grid container spacing={4}>
          {/* Left column (image on desktop, top on mobile) */}
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src={image}
              alt={imageAlt}
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 1,
                boxShadow: 3,
                mb: { xs: 2, md: 0 },
              }}
            />
          </Grid>

          {/* Right column (content) */}
          <Grid item xs={12} md={7}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                color: "#003876",
                borderBottom: "2px solid #d2232a",
                pb: 1,
                mb: 3,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{
                mb: 4,
                lineHeight: 1.7,
              }}
            >
              {content}
            </Typography>

            <Button
              component={RouterLink}
              to={buttonLink}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#d2232a",
                "&:hover": {
                  backgroundColor: "#aa1c22",
                },
                fontWeight: "bold",
                px: 3,
                py: 1,
              }}
            >
              {buttonText}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}

export default PracticeAreaLayout;
